// Industry-organized solutions. Each maps to template categories + a recommended package.
import {
  Church, HandHeart, HeartHandshake, Shield, Store, HardHat,
  UtensilsCrossed, BookOpen, Target, GraduationCap, Users,
} from "lucide-react";

const IMG = {
  church: "https://images.unsplash.com/photo-1522158637959-30385a09e0da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  ministry: "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  nonprofit: "https://images.unsplash.com/photo-1628717341663-0007b0ee2597?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  veteran: "https://images.unsplash.com/photo-1626836014893-37663794dca7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  business: "https://images.unsplash.com/photo-1753164597612-5e71b83fda91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  construction: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  restaurant: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  author: "https://images.unsplash.com/photo-1505330622279-bf7d7fc918f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  coach: "https://images.unsplash.com/photo-1560439514-e960a3ef5019?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  course: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  membership: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const SOLUTIONS = [
  {
    slug: "church-websites", title: "Church Websites", icon: Church, image: IMG.church,
    categories: ["Churches", "Ministries"], recommended: "growth",
    tagline: "Welcome visitors and grow your congregation online.",
    blurb: "Beautiful, visitor-first church websites with sermons, online giving, events, and ministries — built to help people find you and get connected.",
    outcomes: ["Grow attendance & first-time visits", "Enable secure online giving", "Share sermons & events easily"],
    includes: ["Sermon archive & media", "Online giving / donations", "Events & service times", "Ministries & groups", "Prayer request forms", "Mobile-first design"],
  },
  {
    slug: "ministry-websites", title: "Ministry Websites", icon: HandHeart, image: IMG.ministry,
    categories: ["Ministries", "Churches"], recommended: "growth",
    tagline: "Extend your reach and empower your mission.",
    blurb: "Purpose-built ministry sites for teaching, outreach, partner giving, and global impact storytelling.",
    outcomes: ["Grow partners & supporters", "Publish teaching & devotionals", "Tell your impact story"],
    includes: ["Teaching & devotional blog", "Partner / recurring giving", "Outreach programs", "Media & livestream", "Newsletter capture", "Event management"],
  },
  {
    slug: "nonprofit-websites", title: "Nonprofit Websites", icon: HeartHandshake, image: IMG.nonprofit,
    categories: ["Nonprofits"], recommended: "growth",
    tagline: "Turn visitors into donors and volunteers.",
    blurb: "Mission-driven nonprofit websites focused on donations, volunteers, programs, and community trust.",
    outcomes: ["Increase donations", "Recruit volunteers", "Communicate impact clearly"],
    includes: ["Donation CTAs", "Volunteer signup", "Impact stats & stories", "Programs & initiatives", "Annual reports", "Email list growth"],
  },
  {
    slug: "veteran-organizations", title: "Veteran Organizations", icon: Shield, image: IMG.veteran,
    categories: ["Veterans"], recommended: "growth",
    tagline: "Serve those who served — with a hub they can rely on.",
    blurb: "Resource-rich websites for veteran organizations: benefits, events, membership, leadership, and community support.",
    outcomes: ["Centralize member resources", "Grow membership", "Promote events & benefits"],
    includes: ["Resource library", "Member signup", "Benefits links", "Event calendar", "Leadership & board", "Donations / support"],
  },
  {
    slug: "small-business", title: "Small Business", icon: Store, image: IMG.business,
    categories: ["Small Business"], recommended: "growth",
    tagline: "Look established. Win more customers.",
    blurb: "Conversion-focused websites for local and service businesses — built to generate leads and build trust.",
    outcomes: ["Generate qualified leads", "Build credibility fast", "Show up in local search"],
    includes: ["Quote / contact forms", "Service pages", "Testimonials & reviews", "Local SEO setup", "Booking CTAs", "Google Maps integration"],
  },
  {
    slug: "construction", title: "Construction", icon: HardHat, image: IMG.construction,
    categories: ["Contractors"], recommended: "growth",
    tagline: "Showcase your work and land bigger projects.",
    blurb: "Websites for contractors and trades with project galleries, quote requests, service areas, and trust-building reviews.",
    outcomes: ["Win higher-value jobs", "Show off past projects", "Capture quote requests"],
    includes: ["Project gallery", "Quote request forms", "Service areas", "Reviews & badges", "Financing CTAs", "Before/after showcases"],
  },
  {
    slug: "restaurants", title: "Restaurants", icon: UtensilsCrossed, image: IMG.restaurant,
    categories: ["Restaurants"], recommended: "starter",
    tagline: "Make mouths water and fill more tables.",
    blurb: "Appetizing restaurant & catering websites with menus, specials, location, galleries, and catering requests.",
    outcomes: ["Drive reservations & orders", "Promote specials & catering", "Get found locally"],
    includes: ["Digital menu", "Specials & promotions", "Catering requests", "Photo gallery", "Location & hours", "Reservation links"],
  },
  {
    slug: "authors", title: "Authors", icon: BookOpen, image: IMG.author,
    categories: ["Authors", "Personal Brands"], recommended: "starter",
    tagline: "Launch books and grow your readership.",
    blurb: "Author platforms for book launches, reader signups, events, and direct sales.",
    outcomes: ["Sell more books", "Grow your email list", "Promote events & tours"],
    includes: ["Book showcase", "Email capture", "Events & tours", "Blog / news", "Buy links", "Media kit"],
  },
  {
    slug: "coaches", title: "Coaches", icon: Target, image: IMG.coach,
    categories: ["Coaches", "Personal Brands"], recommended: "growth",
    tagline: "Fill your calendar with ideal clients.",
    blurb: "Coaching websites with programs, booking, testimonials, and lead magnets designed to convert.",
    outcomes: ["Book more discovery calls", "Sell programs & packages", "Build authority"],
    includes: ["Program pages", "Online booking", "Lead magnets", "Testimonials & results", "Newsletter", "Payment links"],
  },
  {
    slug: "online-courses", title: "Online Courses", icon: GraduationCap, image: IMG.course,
    categories: ["Coaches", "Personal Brands", "Authors"], recommended: "premium",
    tagline: "Package your knowledge and sell it 24/7.",
    blurb: "Course websites and sales funnels that showcase curriculum, capture leads, and sell enrollments.",
    outcomes: ["Sell course enrollments", "Grow a waitlist", "Deliver a premium experience"],
    includes: ["Curriculum showcase", "Sales & checkout pages", "Waitlist capture", "Student testimonials", "Payment integration", "Drip / access control (custom)"],
  },
  {
    slug: "membership-sites", title: "Membership Sites", icon: Users, image: IMG.membership,
    categories: ["Ecommerce", "Personal Brands"], recommended: "premium",
    tagline: "Build recurring revenue with a members-only experience.",
    blurb: "Membership platforms with gated content, recurring billing, member dashboards, and community features.",
    outcomes: ["Create recurring revenue", "Deliver exclusive content", "Retain engaged members"],
    includes: ["Member sign-up & login", "Recurring billing (custom)", "Gated content areas", "Member dashboard", "Community / forum (custom)", "Tiered plans"],
  },
];

export const getSolution = (slug) => SOLUTIONS.find((s) => s.slug === slug);
